import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Download, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, startOfQuarter, endOfQuarter, subYears } from 'date-fns';

type PeriodType = 'last_month' | 'this_month' | 'quarter' | 'ytd' | 'custom';
type CompareType = 'none' | 'same_period_last_year' | 'previous_period' | 'ytd_to_date';

// Helper function to format currency
const formatCurrency = (amount: number, currency = 'EUR') => {
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const ProfitLossReport: React.FC = () => {
  const [period, setPeriod] = useState<PeriodType>('this_month');
  const [compare, setCompare] = useState<CompareType>('none');
  const [netOfVat, setNetOfVat] = useState(true);
  
  const getPeriodDates = (periodType: PeriodType) => {
    const now = new Date();
    
    switch (periodType) {
      case 'last_month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth)
        };
      case 'this_month':
        return {
          start: startOfMonth(now),
          end: endOfMonth(now)
        };
      case 'quarter':
        return {
          start: startOfQuarter(now),
          end: endOfQuarter(now)
        };
      case 'ytd':
        return {
          start: startOfYear(now),
          end: now
        };
      default:
        return {
          start: startOfMonth(now),
          end: endOfMonth(now)
        };
    }
  };

  const { start: periodStart, end: periodEnd } = getPeriodDates(period);

  const { data: plData, isLoading } = useQuery({
    queryKey: ['profit-loss', period, compare, netOfVat],
    queryFn: async () => {
      // Get GL postings for the period
      const { data: glPostings, error } = await supabase
        .from('gl_postings')
        .select(`
          *,
          chart_of_accounts (
            code,
            name,
            type
          )
        `)
        .gte('date', format(periodStart, 'yyyy-MM-dd'))
        .lte('date', format(periodEnd, 'yyyy-MM-dd'));

      if (error) throw error;

      // Group by account type and calculate totals
      const revenue = glPostings
        ?.filter(p => p.chart_of_accounts?.type === 'Revenue')
        .reduce((sum, p) => sum + (p.credit - p.debit), 0) || 0;

      const expenses = glPostings
        ?.filter(p => p.chart_of_accounts?.type === 'Expense')
        .reduce((sum, p) => sum + (p.debit - p.credit), 0) || 0;

      const cogs = glPostings
        ?.filter(p => p.chart_of_accounts?.type === 'Direct Cost')
        .reduce((sum, p) => sum + (p.debit - p.credit), 0) || 0;

      return {
        revenue,
        cogs,
        grossProfit: revenue - cogs,
        expenses,
        netProfit: revenue - cogs - expenses
      };
    }
  });

  const handleAddAdjustment = async () => {
    // Create adjustment GL posting
    try {
      const { error } = await supabase
        .from('gl_postings')
        .insert([
          {
            date: format(new Date(), 'yyyy-MM-dd'),
            account_id: null, // Would need to select an account
            debit: 0,
            credit: 0,
            source_type: 'ADJUSTMENT',
            source_id: null,
            memo: 'Manual adjustment'
          }
        ]);

      if (error) throw error;
    } catch (error) {
      console.error('Error creating adjustment:', error);
    }
  };

  const handleExportCSV = () => {
    if (!plData) return;

    const csvContent = `Section,Account,Amount
Revenue,Total Revenue,${plData.revenue}
Cost of Goods Sold,Total COGS,${plData.cogs}
Gross Profit,,${plData.grossProfit}
Operating Expenses,Total Expenses,${plData.expenses}
Net Profit,,${plData.netProfit}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profit-loss-${format(periodStart, 'yyyy-MM-dd')}-to-${format(periodEnd, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Profit & Loss</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAddAdjustment}>
            <Plus className="h-4 w-4 mr-2" />
            Add Adjustment
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label htmlFor="period">Period:</Label>
              <Select value={period} onValueChange={(value) => setPeriod(value as PeriodType)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="quarter">Quarter</SelectItem>
                  <SelectItem value="ytd">YTD</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor="compare">Compare:</Label>
              <Select value={compare} onValueChange={(value) => setCompare(value as CompareType)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Comparison</SelectItem>
                  <SelectItem value="same_period_last_year">Same Period Last Year</SelectItem>
                  <SelectItem value="previous_period">Previous Period</SelectItem>
                  <SelectItem value="ytd_to_date">YTD to Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="net-of-vat"
                checked={netOfVat}
                onCheckedChange={setNetOfVat}
              />
              <Label htmlFor="net-of-vat">Use Net of VAT</Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 text-sm text-muted-foreground">
            Period: {format(periodStart, 'MMM dd, yyyy')} - {format(periodEnd, 'MMM dd, yyyy')}
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading P&L data...</div>
          ) : plData ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  {compare !== 'none' && (
                    <>
                      <TableHead className="text-right">Compare</TableHead>
                      <TableHead className="text-right">Δ Amount</TableHead>
                      <TableHead className="text-right">Δ %</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="font-medium bg-muted/50">
                  <TableCell>Revenue</TableCell>
                  <TableCell className="text-right">{formatCurrency(plData.revenue)}</TableCell>
                  {compare !== 'none' && (
                    <>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                    </>
                  )}
                </TableRow>
                
                <TableRow className="font-medium bg-muted/50">
                  <TableCell>Cost of Goods Sold</TableCell>
                  <TableCell className="text-right">{formatCurrency(plData.cogs)}</TableCell>
                  {compare !== 'none' && (
                    <>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                    </>
                  )}
                </TableRow>

                <TableRow className="font-bold border-t">
                  <TableCell>Gross Profit</TableCell>
                  <TableCell className="text-right">{formatCurrency(plData.grossProfit)}</TableCell>
                  {compare !== 'none' && (
                    <>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                    </>
                  )}
                </TableRow>

                <TableRow className="font-medium bg-muted/50">
                  <TableCell>Operating Expenses</TableCell>
                  <TableCell className="text-right">{formatCurrency(plData.expenses)}</TableCell>
                  {compare !== 'none' && (
                    <>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                    </>
                  )}
                </TableRow>

                <TableRow className="font-bold border-t-2 text-lg">
                  <TableCell>Net Profit</TableCell>
                  <TableCell className={`text-right ${plData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(plData.netProfit)}
                  </TableCell>
                  {compare !== 'none' && (
                    <>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right">—</TableCell>
                    </>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No data available for the selected period.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};